import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useSubscriptions, useSubscriptionStats } from "../../../hooks/useSubscriptions";
import type { SubscriptionEntry } from "../../../server/subscriptions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getSubscriptionStats } from "../../../server/subscriptions";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/subscriptions/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.subscriptions.stats(),
      queryFn: async () => {
        const result = await getSubscriptionStats({
          data: { accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Subscriptions"),
  component: SubscriptionsPage,
});

function SubscriptionsPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedSubscriptions accessToken={session.access_token} />;
}

const STATUS_FILTERS = [
  { value: undefined, label: "All" },
  { value: "active" as const, label: "Active" },
  { value: "cancelled" as const, label: "Cancelled" },
  { value: "paused" as const, label: "Paused" },
  { value: "trial" as const, label: "Trial" },
];

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  trial: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

function AuthenticatedSubscriptions({ accessToken }: { accessToken: string }) {
  const auth = { accessToken };
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const limit = 20;

  const { data: stats, isLoading: statsLoading } = useSubscriptionStats(auth);
  const { data, isLoading, error } = useSubscriptions(auth, { page, limit, status: statusFilter });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Subscriptions</h1>
            <p className="text-muted-foreground">
              SaaS subscriptions and recurring billing
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        {/* Stats */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Total Subscriptions</p>
                <p className="text-2xl font-semibold">{stats.totalSubscriptions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Active</p>
                <p className="text-2xl font-semibold">{stats.activeSubscriptions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Monthly Spend</p>
                <p className="text-2xl font-semibold">
                  ${stats.monthlySpend.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="text-2xl font-semibold">{stats.currency}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription List */}
        <Card>
          <CardHeader>
            <CardTitle>All Subscriptions</CardTitle>
            <CardDescription>
              {data ? `${data.total} subscriptions` : "Loading..."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.label}
                  variant={statusFilter === f.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setStatusFilter(f.value);
                    setPage(1);
                  }}
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {isLoading && (
              <p className="text-muted-foreground">Loading subscriptions...</p>
            )}
            {error && (
              <p className="text-destructive">Error: {error.message}</p>
            )}
            {!isLoading && !error && (!data || data.subscriptions.length === 0) && (
              <p className="text-muted-foreground">No subscriptions found.</p>
            )}
            {!isLoading && !error && data && data.subscriptions.length > 0 && (
              <div className="space-y-4">
                <ul className="space-y-2">
                  {data.subscriptions.map((s: SubscriptionEntry) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between rounded-lg border bg-card p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{s.service_name}</p>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] ?? "bg-muted text-muted-foreground"}`}
                          >
                            {s.status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {s.billing_cycle} · {s.service_category ?? "General"}
                          {s.next_billing_date &&
                            ` · Next: ${new Date(s.next_billing_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <p className="text-sm font-semibold whitespace-nowrap ml-4">
                        ${s.cost_amount.toFixed(2)} {s.cost_currency}
                      </p>
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
